import { APP_NATIVE_AD_CLIDS } from '@/constants/general'
import type { PlacementGroup } from '@/modules/adv/ad-service'
import { PLAYGAMA_FALLBACK } from '@/modules/adv/header-bidding/bidders-config'
import { AppNativeSDK } from '@/modules/native-app'
import { type AdProvidersSettings, type PlatformType, type EffectiveType } from '@/types'

import { getCommonProvidersAdSettings } from './common-providers-settings'
import { createAppNativeSettings, createHeaderBiddingAdSettings } from './provider-factories'
import { filterAdProviderSettings } from './utils'

// Native (AppLovin) ad static units — used once the native SDK ships (Android ≥ 1.3.0 / iOS ≥ 1.1.6).
const ANDROID_APPLOVIN_STATIC_AD_UNITS = {
    sidebar: '10463e351bd291e4',
    sidebar_bottom: '2c806b2ff068c4b0',
    sidebar_bottom_fullscreen: '10463e351bd291e4',
    sidebar_fullscreen: '2c806b2ff068c4b0',
    leaderboard: '7f01b3ab73b34ca5',
    leaderboard_rest: '7f01b3ab73b34ca5',
    sticky_mobile: '7f01b3ab73b34ca5',
    sticky_mobile_non_game_page: '7f01b3ab73b34ca5',
    sticky_tablet: '7f01b3ab73b34ca5',
    widget_sticky_tablet: '7f01b3ab73b34ca5',
    widget_sticky_mobile: '7f01b3ab73b34ca5',
}

const IOS_APPLOVIN_STATIC_AD_UNITS = {
    sidebar: 'd1122d34d7bf9788',
    sidebar_bottom: 'eb4462a34a3ebeb0',
    sidebar_bottom_fullscreen: 'd1122d34d7bf9788',
    sidebar_fullscreen: 'eb4462a34a3ebeb0',
    leaderboard: 'e0376ee33bf46aed',
    leaderboard_rest: 'e0376ee33bf46aed',
    sticky_mobile: 'e0376ee33bf46aed',
    sticky_mobile_non_game_page: 'e0376ee33bf46aed',
    sticky_tablet: 'e0376ee33bf46aed',
    widget_sticky_tablet: 'e0376ee33bf46aed',
    widget_sticky_mobile: 'e0376ee33bf46aed',
}

// Native (AppLovin) ad units — used once the native SDK ships (Android ≥ 1.1.09 / iOS ≥ 1.1.3).
const ANDROID_APPLOVIN_AD_UNITS = {
    interstitial: 'adc0a523dde2c2f1',
    rewarded: 'd0ef11af6f54f0fb',
}
const IOS_APPLOVIN_AD_UNITS = {
    interstitial: 'da96af9802e81f8f',
    rewarded: '23193cc48219498c',
}

// GAM fallback ad units for older app versions without the native SDK.
const ANDROID_GAM_AD_UNITS = {
    interstitial: '/23211928466/interstitial_android',
    interstitial_preroll: '/23211928466/interstitial_preroll_android',
    rewarded: '/23211928466/rewarded_android',
}
const IOS_GAM_AD_UNITS = {
    interstitial: '/23211928466/interstitial_ios',
    interstitial_preroll: '/23211928466/interstitial_preroll_ios',
    rewarded: '/23211928466/rewarded_ios',
}
const TEST_GAM_AD_UNITS = {
    interstitial: '/21775744923/example/interstitial',
    interstitial_preroll: '/21775744923/example/interstitial',
    rewarded: '/21775744923/example/rewarded',
}

export const getNativeAppAdSettings = ({
    gamAccount,
    platform,
    isVersionAtLeast,
    isNativeBannersEnabled,
    appNativeSDK,
    clid,
    effectiveType,
}: {
    gamAccount: string
    platform: PlatformType
    isVersionAtLeast: (minVersions: { ios?: string; android?: string }) => boolean
    isNativeBannersEnabled: boolean
    appNativeSDK: AppNativeSDK | undefined
    clid: string
    effectiveType: EffectiveType | undefined
}): PlacementGroup<AdProvidersSettings>[] => {
    const hasApplovinSdk = isVersionAtLeast({ ios: '1.1.3', android: '1.1.09' })
    const androidNativeBannerAdUnits = isNativeBannersEnabled ? ANDROID_APPLOVIN_STATIC_AD_UNITS : {}
    const iosNativeBannerAdUnits = isNativeBannersEnabled ? IOS_APPLOVIN_STATIC_AD_UNITS : {}

    const APP_NATIVE_PROVIDERS = {
        android: [
            createAppNativeSettings(appNativeSDK, platform, {
                adUnitConfig: {
                    mobile: hasApplovinSdk
                        ? { ...ANDROID_APPLOVIN_AD_UNITS, ...androidNativeBannerAdUnits }
                        : ANDROID_GAM_AD_UNITS,
                    desktop: {},
                },
            }),
        ],
        androidTest: [
            createAppNativeSettings(appNativeSDK, platform, {
                adUnitConfig: {
                    mobile: hasApplovinSdk
                        ? { ...ANDROID_APPLOVIN_AD_UNITS, ...androidNativeBannerAdUnits }
                        : TEST_GAM_AD_UNITS,
                    desktop: {},
                },
            }),
        ],
        ios: [
            createAppNativeSettings(appNativeSDK, platform, {
                adUnitConfig: {
                    mobile: hasApplovinSdk ? { ...IOS_APPLOVIN_AD_UNITS, ...iosNativeBannerAdUnits } : IOS_GAM_AD_UNITS,
                    desktop: {},
                },
            }),
        ],
        iosTest: [
            createAppNativeSettings(appNativeSDK, platform, {
                adUnitConfig: {
                    mobile: hasApplovinSdk
                        ? { ...IOS_APPLOVIN_AD_UNITS, ...iosNativeBannerAdUnits }
                        : TEST_GAM_AD_UNITS,
                    desktop: {},
                },
            }),
        ],
    }

    const appNativeWaterfall = APP_NATIVE_AD_CLIDS[clid]
    const appNativeProviders = appNativeWaterfall ? APP_NATIVE_PROVIDERS[appNativeWaterfall] : undefined

    if (isNativeBannersEnabled && appNativeProviders) {
        return [
            {
                placements: ['*'],
                settings: appNativeProviders,
            },
        ]
    }

    const common = getCommonProvidersAdSettings(gamAccount, platform)

    return [
        {
            placements: ['*'],
            settings: [common.GOOGLE_AD_PORTAL_SETTINGS],
        },
        {
            placements: ['interstitial', 'interstitial_midroll', 'interstitial_preroll', 'rewarded'],
            settings: [
                ...(appNativeProviders ||
                    filterAdProviderSettings(effectiveType, [
                        common.GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT,
                        common.GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
                        common.GOOGLE_AD_GAME_PAGE_SETTINGS_INTERSTITIAL_FORMAT,
                        common.GOOGLE_BANNER_GAME_PAGE_SETTINGS,
                    ])),
                createHeaderBiddingAdSettings(platform, [PLAYGAMA_FALLBACK]),
            ],
        },
    ]
}
